import React, { useState } from "react";
import { DownloadSimple, DeviceMobile, X, ShareNetwork, Plus } from "@phosphor-icons/react";
import { usePWAInstall } from "@/hooks/usePWAInstall";

/**
 * InstallBanner — full-width prominent CTA for shop floor operators.
 * Renders only when the app is installable and the user hasn't dismissed it,
 * and only on non-standalone browsers.
 */
export function InstallBanner() {
  const { canInstall, isStandalone, platform, promptInstall, dismissed, dismiss, hasNativePrompt } = usePWAInstall();
  const [showIOS, setShowIOS] = useState(false);

  if (isStandalone || dismissed || !canInstall) return null;

  const onClick = async () => {
    if (hasNativePrompt) {
      await promptInstall();
    } else if (platform === "ios") {
      setShowIOS(true);
    }
  };

  return (
    <>
      <div
        className="w-full bg-foreground text-background border-b border-foreground/10"
        data-testid="install-banner"
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 border border-background/30 flex items-center justify-center shrink-0">
            <DeviceMobile size={18} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase font-bold text-background/60">Shop Floor Ready</div>
            <div className="text-sm font-semibold truncate">Install SLKC on your phone — log cutting &amp; stitching without a browser.</div>
          </div>
          <button
            onClick={onClick}
            data-testid="install-banner-cta"
            className="h-9 px-4 bg-background text-foreground font-mono text-[11px] tracking-[0.15em] uppercase font-bold hover:-translate-y-[1px] transition-transform flex items-center gap-2 shrink-0"
          >
            <DownloadSimple size={14} weight="bold" />
            Install App
          </button>
          <button
            onClick={dismiss}
            data-testid="install-banner-dismiss"
            className="w-9 h-9 border border-background/30 flex items-center justify-center hover:bg-background/10 shrink-0"
            aria-label="Dismiss"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      </div>
      {showIOS && <IOSInstructions onClose={() => setShowIOS(false)} />}
    </>
  );
}

/**
 * InstallButton — small icon-only button that fits in the top bar / header.
 * Hidden entirely when already installed.
 */
export function InstallButton({ className = "", testid = "install-button" }) {
  const { canInstall, isStandalone, platform, promptInstall, hasNativePrompt } = usePWAInstall();
  const [showIOS, setShowIOS] = useState(false);

  if (isStandalone || !canInstall) return null;

  const onClick = async () => {
    if (hasNativePrompt) {
      await promptInstall();
    } else if (platform === "ios") {
      setShowIOS(true);
    }
  };

  return (
    <>
      <button
        onClick={onClick}
        data-testid={testid}
        title="Install SLKC as an app"
        aria-label="Install app"
        className={`h-9 px-3 border border-border bg-white hover:bg-secondary flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] uppercase font-bold ${className}`}
      >
        <DownloadSimple size={13} weight="bold" />
        <span className="hidden sm:inline">Install</span>
      </button>
      {showIOS && <IOSInstructions onClose={() => setShowIOS(false)} />}
    </>
  );
}

function IOSInstructions({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-foreground/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="ios-install-instructions"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-white border border-border shadow-2xl"
      >
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="label-caps">Add to Home Screen</div>
            <div className="font-display font-black text-xl mt-1">Install on iPhone</div>
          </div>
          <button
            onClick={onClose}
            data-testid="ios-install-close"
            className="w-9 h-9 border border-border flex items-center justify-center hover:bg-secondary"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
        <ol className="p-5 space-y-4 text-sm">
          <li className="flex items-start gap-3">
            <div className="w-7 h-7 bg-foreground text-background font-mono text-xs font-bold flex items-center justify-center shrink-0">1</div>
            <div>
              Tap the <ShareNetwork size={14} weight="bold" className="inline mx-1" /> <b>Share</b> button in Safari&apos;s bottom bar.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-7 h-7 bg-foreground text-background font-mono text-xs font-bold flex items-center justify-center shrink-0">2</div>
            <div>
              Scroll and pick <b>Add to Home Screen</b> <Plus size={14} weight="bold" className="inline mx-1" />.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-7 h-7 bg-foreground text-background font-mono text-xs font-bold flex items-center justify-center shrink-0">3</div>
            <div>Confirm the name <b>SLKC</b> and tap <b>Add</b>.</div>
          </li>
        </ol>
        <div className="px-5 pb-5">
          <button
            onClick={onClose}
            data-testid="ios-install-got-it"
            className="w-full h-10 bg-foreground text-background font-mono text-[11px] tracking-[0.2em] uppercase font-bold"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
